// Small helpers for formatting
export const fmtMoney = (n, currency = "EUR") => {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(n);
};

// Compact "millions" formatter used in roster card stats.
// Accepts either a millions value (≤999, e.g. 2.5) or a raw EUR value
// (e.g. 550_000 / 29_000_000) and produces a clean display:
//   null/NaN  → "—"
//   0         → "—"   (most clients without TM data — show dash, not "€0")
//   < 1M      → "€XXXk" (e.g. 550_000 → "€550k")
//   ≥ 1M      → "€X.XM" (e.g. 29_000_000 → "€29M", 2.5 → "€2.5M")
export const fmtMillions = (v) => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (!n) return "—";
  // Heuristic: anything <1000 is already in millions; ≥1000 is raw EUR.
  const eur = n < 1000 ? n * 1_000_000 : n;
  if (eur < 1_000_000) {
    return `€${(eur / 1000).toFixed(0)}k`;
  }
  const m = eur / 1_000_000;
  return `€${m >= 10 ? m.toFixed(0) : m.toFixed(1)}M`;
};

// ── Market value — single source of truth ────────────────────────────────
// Use this everywhere the player market value is displayed. Input is the
// raw EUR amount that the backend resolves from `integration_cache`
// (provider='transfermarkt'). Never format market value inline.
//   null/0      → "—"   (never "€0M", never "€M")
//   < 1k        → "€XYZ"
//   < 1M        → "€XYZk"
//   exact M     → "€XM"  (e.g. 3_000_000 → "€3M")
//   fractional  → "€X.XM" (e.g. 3_500_000 → "€3.5M")
export const formatMarketValue = (euros) => {
  if (euros == null || isNaN(euros)) return "—";
  const n = Number(euros);
  if (!n) return "—";
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return m % 1 === 0 ? `€${m}M` : `€${m.toFixed(1)}M`;
  }
  if (n >= 1_000) return `€${Math.round(n / 1_000)}k`;
  return `€${n.toLocaleString()}`;
};

// Compact relative-time helper for the "updated X days ago" Transfermarkt
// freshness label. Never shows the future tense.
export const fmtRelativeDays = (iso) => {
  if (!iso) return null;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return "just now";
    const days = Math.floor(ms / 86400000);
    if (days < 1) {
      const hrs = Math.floor(ms / 3600000);
      if (hrs < 1) return "just now";
      return `${hrs}h ago`;
    }
    if (days === 1) return "1 day ago";
    if (days < 30) return `${days} days ago`;
    if (days < 365) return `${Math.floor(days / 30)} months ago`;
    return `${Math.floor(days / 365)} years ago`;
  } catch {
    return null;
  }
};

export const fmtNumber = (n) => {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-GB").format(n);
};

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const daysBetween = (a, b) => {
  if (!a || !b) return null;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};
